/**
 * wtshuang@cs.uw.edu
 * 2018/01/17
 * The panel for the paragraph. Integrated in instance panel.
 */

import * as React from 'react';
import * as d3 from 'd3';

import { Message, Popup } from 'semantic-ui-react';

import { Answer } from '../stores/Answer';
import { Token, FlagDatum } from '../stores/Interfaces';
import { utils } from '../stores/Utils';

export interface ParagraphProps {
    qid: string;
    qtokens: Token[]; // the qid
    answers: Answer[];
    ptokens: Token[];
    selectedToken: Token|null;
    attention: number[][];
    hoveredIdx: number;
    displayBlogFlags: FlagDatum[];

    mouseoveredHandler: (idx: number) => void;
    mouseoutHandler: () => void;
}

export class ParagraphPanel extends React.Component<ParagraphProps, {}> {
    // view related
    private viewType: string;
    // imported from the upper view
    private qid: string;
    private qtokens: Token[];
    private ptokens: Token[];
    private answers: Answer[];
    private attention: number[][];
    private selectedToken: Token|null;
    private colorScale: {
        entity: d3.ScaleQuantile<string>;
        others: d3.ScaleQuantile<string>;
     } 

    constructor (props: ParagraphProps, context: any) {
        super(props, context);
        this.viewType = 'ppanel';
        this.ptokens = this.props.ptokens;
        this.qtokens = this.props.qtokens;
        this.qid = this.props.qid;
        this.answers = this.props.answers;
        this.attention = this.props.attention;
        this.selectedToken = this.props.selectedToken;
        this.colorScale = {
            entity: d3.scaleQuantile<string>().domain([0, 1.01]).range(utils.attentionColor.entity),
            others: d3.scaleQuantile<string>().domain([0, 1.01]).range(utils.attentionColor.others)
        }
    }

    /**
     * get the color for a given t, based on the currently selected answer
     * @param t <Token> the targeting token.
     * @param answers <Answer[]> the answers
     * @return <string> a color.
     */
    private answerColorFunc (t: Token, answers: Answer[]): string {
        let color = 'black';
        if (answers !== undefined) {
            answers.forEach(a => {
                if (a !== undefined && a.span_start <= t.idx && a.span_end > t.idx) {
                    color = a.getColorDark();
                }
            });
        }
        return color;
    }
    

    /**
     * get the background color for a given t, based on the currently selected answer
     * @param t <Token> the targeting token.
     * @return <string> a color.
     */
    private answerBackgroundColorFunc (t: Token): string {
        let color = 'rgba(255, 255, 255, 0)';
        const tokenAttentions = t.idx >= this.ptokens.length ? this.attention[0] : this.attention[t.idx];
        let maxQTokenIdx: number;
        
        if (tokenAttentions === undefined || this.selectedToken === null) {
            return color;
        }
        if (this.selectedToken === null) {
            maxQTokenIdx = utils.argMax(tokenAttentions);
        } else {
            maxQTokenIdx = this.selectedToken.idx;
        }
        if ((tokenAttentions[maxQTokenIdx] < 0.5 || this.qtokens[maxQTokenIdx].pos === 'PUNCT')) {
        } else {
            color = this.colorScale.others(tokenAttentions[maxQTokenIdx]);
        }
        return color;
    }

    public renderTokens(sentenceGroups: {[key: string]: Token[]}, sid: string): JSX.Element {
        return <p>{sentenceGroups[sid].map((t: Token, idx: number) => {
            // generate the current class for the token
            const curClass = utils.genClass(this.viewType, 'ptoken', [this.qid, utils.genStrId(t.text), t.idx]);
            const hovered = (t.idx === this.props.hoveredIdx) ? 'hovered' : '';
            let answerToken: string = '';
            this.answers.forEach(a => {
                if (a !== undefined && a.span_start <= t.idx && a.span_end > t.idx) {
                    answerToken = 'answer'
                }
            });
            let annotation: string = '';

            for (let i = 0; i < this.props.displayBlogFlags.length; i++) {
                const flg = this.props.displayBlogFlags[i];
                annotation = t[flg.ftype] !== flg.value ? '' : idx > 0 
                    && sentenceGroups[sid][idx-1].ner !== t[flg.ftype] ? t[flg.ftype] : '';
            }
            // get the current span
            const curSpan: JSX.Element = <span 
                key={ curClass.key }
                className={ `token ${curClass.total} ${hovered} ${answerToken}` }
                id={ curClass.key }>
                    <div style={{paddingTop: 1, verticalAlign: 'bottom'}} className='token-info-block'>{annotation.toUpperCase()}</div>
                    <div style={{paddingTop: 0, marginTop: -5, paddingBottom: 0, marginBottom: 0,
                            color: this.answerColorFunc(t, this.answers),
                            backgroundColor: this.answerBackgroundColorFunc(t)}}
                            onMouseOut={() => {this.props.mouseoutHandler()}}
                            onMouseOver={() => {this.props.mouseoveredHandler(t.idx)}}>
                    { t.text + t.whitespace }</div>
                    </span>;
            const popup = <span>
                <b>NER: </b>{t.ner} | 
                <b>POS: </b>{t.pos} | 
                <b>LEMMA: </b>{t.lemma}</span>;

            return <span key={ `${curClass.key}-popup` }>
                <Popup position='top center' size='mini' inverted
                    trigger={curSpan} content={popup} />
                </span>
        })}</p>
    }

    /**
     * Render the paragraph.
     */
    public render (): JSX.Element {
        const sentenceGroups: {[key: string]: Token[]} = utils.groupBy<Token>(this.ptokens, 'sid');
        const sids = Object.keys(sentenceGroups).sort((a, b) => parseInt(a, 10) - parseInt(b));
        const msg = <div className='full-width'>{sids.map((sid: string) => {
            return  this.renderTokens(sentenceGroups, sid)} 
            )}</div>
        return (
            <Message className={ `full-width` }>
                {/*<Message.Header>{this.paragraph.title}</Message.Header>*/}
                {msg}
            </Message>);
    }
}


// WEBPACK FOOTER //
// ./src/components/ParagraphPanel.tsx